import React, { useMemo, useState } from "react";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

function todayFR() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function clean(s) {
  return String(s ?? "").trim();
}

async function exportAttestationPdf(data) {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([595.28, 841.89]); // A4
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const margin = 56;
  let y = 841.89 - margin;

  const draw = (text, opts = {}) => {
    const size = opts.size ?? 12;
    page.drawText(String(text), {
      x: opts.x ?? margin,
      y,
      size,
      font: opts.bold ? bold : font,
      color: opts.color ?? rgb(0, 0, 0),
      maxWidth: 595.28 - margin * 2,
      lineHeight: size + 3,
    });
    y -= size + (opts.gap ?? 8);
  };

  // Title
  draw("ATTESTATION D’HÉBERGEMENT", { bold: true, size: 18, gap: 14 });

  // Intro
  draw(
    `Je soussigné(e) ${data.hostLastName} ${data.hostFirstName}, né(e) le ${data.hostBirthDate} à ${data.hostBirthPlace},`
  );
  draw(
    `demeurant au ${data.addressStreet}, ${data.addressZip} ${data.addressCity},`
  );

  y -= 4;

  draw(`atteste sur l’honneur héberger à mon domicile :`, { bold: true });

  draw(
    `${data.guestLastName} ${data.guestFirstName}, né(e) le ${data.guestBirthDate} à ${data.guestBirthPlace}.`
  );

  y -= 6;

  const fromTo =
    data.fromDate && data.toDate
      ? `du ${data.fromDate} au ${data.toDate}`
      : data.fromDate
      ? `à compter du ${data.fromDate}`
      : `à ce jour`;

  draw(`Hébergement ${fromTo}.`);

  y -= 10;

  draw("Fait pour servir et valoir ce que de droit.", { bold: true });

  y -= 10;

  draw(`Fait à ${data.citySigned}, le ${data.dateSigned}.`);

  y -= 30;

  draw("Signature de l’hébergeant(e) :", { bold: true });
  y -= 70;

  // signature line
  page.drawLine({
    start: { x: margin, y: y },
    end: { x: 595.28 - margin, y: y },
    thickness: 1,
    color: rgb(0.2, 0.2, 0.2),
  });

  y -= 18;

  // Notes (small)
  page.drawText(
    "Pièces à joindre (souvent demandées) : copie pièce d’identité de l’hébergeant + justificatif de domicile récent.",
    { x: margin, y: 60, size: 9, font, color: rgb(0.25, 0.25, 0.25) }
  );

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "attestation-hebergement.pdf";
  a.click();
  URL.revokeObjectURL(url);
}

export default function GenerateurAttestationHebergement() {
  // Hébergeant
  const [hostFirstName, setHostFirstName] = useState("");
  const [hostLastName, setHostLastName] = useState("");
  const [hostBirthDate, setHostBirthDate] = useState("");
  const [hostBirthPlace, setHostBirthPlace] = useState("");

  // Adresse
  const [addressStreet, setAddressStreet] = useState("");
  const [addressZip, setAddressZip] = useState("");
  const [addressCity, setAddressCity] = useState("");

  // Hébergé
  const [guestFirstName, setGuestFirstName] = useState("");
  const [guestLastName, setGuestLastName] = useState("");
  const [guestBirthDate, setGuestBirthDate] = useState("");
  const [guestBirthPlace, setGuestBirthPlace] = useState("");

  // Dates
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Signature
  const [citySigned, setCitySigned] = useState("");
  const [dateSigned, setDateSigned] = useState(todayFR());

  const data = useMemo(() => {
    return {
      hostFirstName: clean(hostFirstName),
      hostLastName: clean(hostLastName),
      hostBirthDate: clean(hostBirthDate),
      hostBirthPlace: clean(hostBirthPlace),
      addressStreet: clean(addressStreet),
      addressZip: clean(addressZip),
      addressCity: clean(addressCity),
      guestFirstName: clean(guestFirstName),
      guestLastName: clean(guestLastName),
      guestBirthDate: clean(guestBirthDate),
      guestBirthPlace: clean(guestBirthPlace),
      fromDate: clean(fromDate),
      toDate: clean(toDate),
      citySigned: clean(citySigned),
      dateSigned: clean(dateSigned) || todayFR(),
    };
  }, [
    hostFirstName,
    hostLastName,
    hostBirthDate,
    hostBirthPlace,
    addressStreet,
    addressZip,
    addressCity,
    guestFirstName,
    guestLastName,
    guestBirthDate,
    guestBirthPlace,
    fromDate,
    toDate,
    citySigned,
    dateSigned,
  ]);

  const isValid =
    data.hostFirstName &&
    data.hostLastName &&
    data.hostBirthDate &&
    data.hostBirthPlace &&
    data.addressStreet &&
    data.addressZip &&
    data.addressCity &&
    data.guestFirstName &&
    data.guestLastName &&
    data.guestBirthDate &&
    data.guestBirthPlace &&
    data.citySigned &&
    data.dateSigned;

  return (
    <div className="page">
      <div className="hero">
        <h1>Attestation d’hébergement (PDF)</h1>
        <p className="muted" style={{ margin: 0 }}>
          Génère une attestation prête à imprimer. Calcul 100% local (aucune
          donnée envoyée).
        </p>
      </div>

      <div style={{ height: 16 }} />

      <div className="grid-2">
        {/* Formulaire */}
        <div className="card">
          <h2 className="cardTitle">Informations</h2>

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Hébergeant(e)
          </h3>

          <div className="grid-2">
            <label className="label">
              Prénom
              <input
                className="input"
                value={hostFirstName}
                onChange={(e) => setHostFirstName(e.target.value)}
              />
            </label>
            <label className="label">
              Nom
              <input
                className="input"
                value={hostLastName}
                onChange={(e) => setHostLastName(e.target.value)}
              />
            </label>
          </div>

          <div className="grid-2">
            <label className="label">
              Date de naissance (JJ/MM/AAAA)
              <input
                className="input"
                value={hostBirthDate}
                onChange={(e) => setHostBirthDate(e.target.value)}
              />
            </label>
            <label className="label">
              Lieu de naissance
              <input
                className="input"
                value={hostBirthPlace}
                onChange={(e) => setHostBirthPlace(e.target.value)}
              />
            </label>
          </div>

          <div style={{ height: 8 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Adresse du logement
          </h3>

          <label className="label">
            Adresse (numéro + rue)
            <input
              className="input"
              value={addressStreet}
              onChange={(e) => setAddressStreet(e.target.value)}
            />
          </label>

          <div className="grid-2">
            <label className="label">
              Code postal
              <input
                className="input"
                value={addressZip}
                onChange={(e) => setAddressZip(e.target.value)}
                inputMode="numeric"
              />
            </label>
            <label className="label">
              Ville
              <input
                className="input"
                value={addressCity}
                onChange={(e) => setAddressCity(e.target.value)}
              />
            </label>
          </div>

          <div style={{ height: 8 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Hébergé(e)
          </h3>

          <div className="grid-2">
            <label className="label">
              Prénom
              <input
                className="input"
                value={guestFirstName}
                onChange={(e) => setGuestFirstName(e.target.value)}
              />
            </label>
            <label className="label">
              Nom
              <input
                className="input"
                value={guestLastName}
                onChange={(e) => setGuestLastName(e.target.value)}
              />
            </label>
          </div>

          <div className="grid-2">
            <label className="label">
              Date de naissance (JJ/MM/AAAA)
              <input
                className="input"
                value={guestBirthDate}
                onChange={(e) => setGuestBirthDate(e.target.value)}
              />
            </label>
            <label className="label">
              Lieu de naissance
              <input
                className="input"
                value={guestBirthPlace}
                onChange={(e) => setGuestBirthPlace(e.target.value)}
              />
            </label>
          </div>

          <div style={{ height: 8 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Période d’hébergement (option)
          </h3>

          <div className="grid-2">
            <label className="label">
              À partir du (JJ/MM/AAAA)
              <input
                className="input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>
            <label className="label">
              Jusqu’au (JJ/MM/AAAA)
              <input
                className="input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>
          </div>

          <div style={{ height: 8 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Signature
          </h3>

          <div className="grid-2">
            <label className="label">
              Fait à (ville)
              <input
                className="input"
                value={citySigned}
                onChange={(e) => setCitySigned(e.target.value)}
              />
            </label>
            <label className="label">
              Date (JJ/MM/AAAA)
              <input
                className="input"
                value={dateSigned}
                onChange={(e) => setDateSigned(e.target.value)}
              />
            </label>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="btn"
              disabled={!isValid}
              onClick={() => exportAttestationPdf(data)}
            >
              Télécharger PDF
            </button>

            <button
              className="btn btnSecondary"
              onClick={() => {
                setHostFirstName("");
                setHostLastName("");
                setHostBirthDate("");
                setHostBirthPlace("");
                setAddressStreet("");
                setAddressZip("");
                setAddressCity("");
                setGuestFirstName("");
                setGuestLastName("");
                setGuestBirthDate("");
                setGuestBirthPlace("");
                setFromDate("");
                setToDate("");
                setCitySigned("");
                setDateSigned(todayFR());
              }}
            >
              Réinitialiser
            </button>
          </div>

          {!isValid && (
            <div className="small" style={{ marginTop: 10 }}>
              Remplis tous les champs principaux (identités, adresse, lieu +
              date de signature).
            </div>
          )}
        </div>

        {/* Aperçu */}
        <div className="card">
          <h2 className="cardTitle">Aperçu</h2>

          <div
            className="card"
            style={{ padding: 12, background: "rgba(255,255,255,.06)" }}
          >
            <p style={{ marginTop: 0 }}>
              <strong>ATTESTATION D’HÉBERGEMENT</strong>
            </p>

            <p style={{ marginBottom: 10, opacity: 0.85 }}>
              Je soussigné(e) <strong>{data.hostLastName || "Nom"}</strong>{" "}
              <strong>{data.hostFirstName || "Prénom"}</strong>, né(e) le{" "}
              <strong>{data.hostBirthDate || "JJ/MM/AAAA"}</strong> à{" "}
              <strong>{data.hostBirthPlace || "Ville"}</strong>, demeurant au{" "}
              <strong>{data.addressStreet || "Adresse"}</strong>,{" "}
              <strong>{data.addressZip || "CP"}</strong>{" "}
              <strong>{data.addressCity || "Ville"}</strong>, atteste sur
              l’honneur héberger à mon domicile :
            </p>

            <p style={{ marginTop: 0, marginBottom: 10, opacity: 0.85 }}>
              <strong>{data.guestLastName || "Nom"}</strong>{" "}
              <strong>{data.guestFirstName || "Prénom"}</strong>, né(e) le{" "}
              <strong>{data.guestBirthDate || "JJ/MM/AAAA"}</strong> à{" "}
              <strong>{data.guestBirthPlace || "Ville"}</strong>.
            </p>

            <p style={{ marginTop: 0, marginBottom: 10, opacity: 0.85 }}>
              Hébergement{" "}
              <strong>
                {data.fromDate && data.toDate
                  ? `du ${data.fromDate} au ${data.toDate}`
                  : data.fromDate
                  ? `à compter du ${data.fromDate}`
                  : `à ce jour`}
              </strong>
              .
            </p>

            <p style={{ marginTop: 0, marginBottom: 10, opacity: 0.85 }}>
              Fait pour servir et valoir ce que de droit.
            </p>

            <p style={{ marginTop: 0, marginBottom: 0, opacity: 0.85 }}>
              Fait à <strong>{data.citySigned || "Ville"}</strong>, le{" "}
              <strong>{data.dateSigned || todayFR()}</strong>.
            </p>

            <div className="small" style={{ marginTop: 12 }}>
              Pièces souvent demandées : pièce d’identité hébergeant +
              justificatif de domicile récent.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
